import OLMap from 'ol/Map';
import CanvasVectorLayerRenderer from 'ol/renderer/canvas/VectorLayer';
import Event from 'ol/events/Event';
import { register as registerProj4 } from 'ol/proj/proj4';

import { Oval } from 'cadenza/map/geom/oval';
import { Rectangle } from 'cadenza/map/geom/rectangle';
import { MapView } from 'cadenza/map/map-view';
import { calculateMapViewPadding } from 'cadenza/map/map-utils';
import { proj4 } from 'cadenza/map/projection-utils';
import type { DimensionDto } from 'cadenza/api-client/dto/dimension-dto';

registerProj4(proj4);

export interface MapConfig {
  mapControls?: boolean;

  map: {
    hidpi?: boolean;
  };
}

export class Map extends OLMap {

  /**
   * Get the size of this map without view padding.
   * Map toolbar overlays the map, because it can be hidden.
   * Sometimes we need the size of the map without the toolbar.
   *
   * @return The size in pixels of the map in the DOM with view padding subtracted.
   */
  getInnerSize (): DimensionDto {
    const view = this.getView();
    /** Check required as during initialization `getView` is "only" an OL `View`, not a `MapView`. */
    if (view && view instanceof MapView) {
      return view.getViewportSizeMinusPadding();
    }
    // Fallback during initialization, when the map view is not set yet
    let [ width, height ] = super.getSize() ?? [ 0, 0 ]; // in case map container is hidden, in some browsers [0, 0] is returned, and in some - undefined.
    if (width === 0 || height === 0) {
      // Map size 0 usually means the map is not visible (for example because the whole view is not displayed),
      // we make sure positive values are returned in such case. [100, 100] is also OL's default for a viewport size.
      width = 100;
      height = 100;
    }
    const [ paddingTop, paddingRight, paddingBottom, paddingLeft ] = calculateMapViewPadding(this);
    return [ width - paddingRight - paddingLeft, height - paddingTop - paddingBottom ];
  }

  override set (key: string, value: unknown, isSilent?: boolean) {
    const hasPropagated = this.dispatchEvent(Object.assign(new Event('changing'), { key, value }));
    if (hasPropagated || hasPropagated === undefined) {
      super.set(key, value, isSilent);
    }
  }

  /**
   * During initialization `getView` is "only" an OL `View`, not a `MapView`.
   * To not burden all callers with an initialization special-case, we type this as `MapView`.
   */
  override getView (): MapView {
    return super.getView() as MapView;
  }

  override getTargetElement () {
    const el = super.getTargetElement();
    if (el) {
      // Make the map keyboard-accessible
      if (!el.hasAttribute('tabindex')) {
        el.tabIndex = 0;
      }
      if (el.tabIndex >= 0) {
        el.classList.add('focus-outline-keynav');
      }
    }
    return el;
  }

}

// OpenLayers rendering implementation does not support custom types (see GEOMETRY_RENDERERS in ol/renderer/vector.js).
// That's why custom geometry types should pretend to be some OpenLayers standard type.
const originalRenderFeature = CanvasVectorLayerRenderer.prototype.renderFeature;
CanvasVectorLayerRenderer.prototype.renderFeature = function (feature, squaredTolerance, styles, builderGroup, optTransform) {
  const geometry = feature.getGeometry();
  if (geometry instanceof Oval) {
    geometry.pretendToBeLineString = true;
  } else if (geometry instanceof Rectangle) {
    geometry.pretendToBePolygon = true;
  }

  const loading = originalRenderFeature.call(this, feature, squaredTolerance, styles, builderGroup, optTransform);

  if (geometry instanceof Oval) {
    geometry.pretendToBeLineString = false;
  } else if (geometry instanceof Rectangle) {
    geometry.pretendToBePolygon = false;
  }
  return loading;
};
